import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import {
  View,
  Views,
  Event as EventCalendar,
  SlotInfo,
  NavigateAction,
  Navigate,
} from 'react-big-calendar';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';

import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';
import { debounce } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { isRecurringEvent } from 'APP/main/Calendar/Content/utils/isRecurringEvent';
import {
  ICalendarAuthAccount,
  ConfirmationType,
  IEvent,
  IParticipant,
  IUpdateCalendarEventParams,
  IUpdateRecurrenceCalendarEventParams,
} from 'APP/model/calendar/calendar.types';
import EventBus from 'APP/packages/bus';
import { storage } from 'APP/packages/storage';
import { checkAllDayRange } from 'APP/utils/calendar/checkAllDayRange';
import { getAdditionalRanges } from 'APP/utils/calendar/getAdditionalRanges';
import { getVisibleRange } from 'APP/utils/calendar/getVisibleRange';
import { normalizeAllDay } from 'APP/utils/calendar/normalizeAllDay';
import Entities from 'STORE';

import { IInitialCalendarParams } from '../Calendar.types';
import { GET_EVENTS_DELAY, STEP } from './Content.constants';
import { IContentGridCalendarEvent, IEventCalendar, IRange, IUpdateEvent } from './Content.types';
import {
  IActiveSidebarViewPresenter,
  useActiveSidebarViewPresenter,
} from './Presenters/ActiveSidebarView.presenter';
import { IDragAndDropPresenter, useDragAndDropPresenter } from './Presenters/DragAndDrop.presenter';
import {
  IScrollToFirstEventPresenter,
  useScrollToFirstEventPresenter,
} from './Presenters/ScrollToFirstEvent.presenter';
import { RepeatEnum } from './RightSidebar/Form/Form.constants';

interface IContentPresenter {
  currentView: View;
  currentDate: Date;
  currentRange: { start: Date; end: Date } | null;
  currentAction: NavigateAction;
  isLoading: boolean;
  data: IEventCalendar[];
  events: IEventCalendar[];
  gridEvents: IContentGridCalendarEvent[];
  rangesMap: { [key: string]: IEventCalendar[] | null };
  currentSelectedEditEvent: IEventCalendar | null;
  templateEvent: IEventCalendar | null;
  selectedEvent: IEventCalendar | undefined;
  initialCalendarParams: IInitialCalendarParams;
  initialize(initialCalendarParams: IInitialCalendarParams): void;
  getEvents: (start: Date, end: Date) => Promise<void>;
  addEvent(event: IEvent): void;
  updateEvent(updatedEvent: IEvent): void;
  deleteEventById(id: string): void;
  deleteEventsByParentId(parentId: string, fromDate?: Date): void;
  reloadEvents(): void;
  getEventsByRanges(start: Date, end: Date, action?: NavigateAction): void;
  updateEventsAfterUpdateRecurrenceSuccess(updateEvent: IEvent, hideSidebar?: boolean): void;
  addEventsAfterCreateRecurrenceSuccess(): void;
  updateEventAfterUpdateSuccess(updateEvent: IEvent, hideSidebar?: boolean): void;
  updateEventAfterUpdateParticipantStatusSuccess(event: IEventCalendar): void;
  addEventAfterCreateSuccess(newEvent: IEvent): void;
  deleteEventsAfterDeleteRecurrenceSuccess(id: string): void;
  deleteEventAfterDeleteSuccess(id: string): void;
  changeView(view: View): void;
  onNavigate(date: Date, view: View, action: NavigateAction): void;
  onDrillDown(date: Date): void;
  jumpTo(date: Date): void;
  openDay(date: Date): void;
  onSelectSlot(info: SlotInfo): void;
  onSelectEvent(selectedEvent: EventCalendar): void;
  onRangeChange(range: IRange): void;
  onEditEvent(
    event: IUpdateRecurrenceCalendarEventParams | IUpdateCalendarEventParams,
    hideSidebar?: boolean
  ): Promise<boolean>;
  onEditEventAfterDrop(dragAndDropData: EventInteractionArgs<IEventCalendar>): Promise<void>;
  deleteTemplateEvent(): void;
  deleteSelectedEvent(): void;
  restoreSelectedEvent(): void;
  updateTemplateEvent(params: IUpdateEvent): void;
  updateSelectedEvent(params: IUpdateEvent): void;
  onCloseViewSidebar(): void;
  onInfoViewSidebar(): void;
  onCreate(): void;
  isDayView: boolean;
  isWeekView: boolean;
  isTimeGrid: boolean;
  isInitiator: boolean;
  accounts: ICalendarAuthAccount[];
  getMyEventStatus(event: IParticipant[], providerEmail: string): ConfirmationType;
  myEventStatusBySelectedEvent: ConfirmationType;
  isCompact?: boolean;
  minimizeSideBar(): void;
  isSideBarMinimized: boolean;
  handleCreateRealtimeEvent(event: IEvent): void;
  handleUpdateRealtimeEvent(event: IEvent): void;
  handleDeleteRealtimeEvent(event: IEvent): void;
}

interface IContentPresenterReturnType {
  presenter: IContentPresenter;
  presenterActiveSidebarView: IActiveSidebarViewPresenter;
  presenterDragAndDrop: IDragAndDropPresenter;
  presenterScrollToFirstEvent: IScrollToFirstEventPresenter;
}

interface IContentPresenterParams {
  isCompact?: boolean;
}

export const useContentPresenter = (
  props: IContentPresenterParams
): IContentPresenterReturnType => {
  const { isCompact } = props;
  const { t } = useTranslation();
  const presenterActiveSidebarView = useActiveSidebarViewPresenter();
  const presenterDragAndDrop = useDragAndDropPresenter();
  const presenterScrollToFirstEvent = useScrollToFirstEventPresenter();

  const presenter = useLocalObservable<IContentPresenter>(() => ({
    currentView: Views.WEEK,
    currentDate: new Date(),
    currentRange: null,
    currentAction: Navigate.NEXT,
    currentSelectedEditEvent: null,
    accounts: [],
    data: [],
    isLoading: false,
    rangesMap: {},
    isSideBarMinimized: false,
    templateEvent: null,

    get initialCalendarParams(): IInitialCalendarParams {
      return Entities.calendar.initialCalendarParams;
    },

    initialize(initialCalendarParams: IInitialCalendarParams): void {
      const { eventsMap, currentDate, currentView, accounts } = initialCalendarParams;

      if (currentDate) {
        presenter.currentDate = currentDate;
      }

      if (currentView) {
        presenter.currentView = isCompact ? Views.DAY : currentView;
      }

      presenter.accounts = accounts;

      if (!eventsMap) {
        return;
      }

      presenter.data = Object.values(eventsMap)[0] || [];
      presenter.rangesMap = { ...eventsMap };

      const [startStr, endStr] = Object.keys(eventsMap)[0].split('-');
      const startDate = new Date(+startStr);
      const endDate = new Date(+endStr);

      presenter.currentRange = { start: startDate, end: endDate };

      presenter.getEventsByRanges(startDate, endDate);
    },

    async getEvents(start: Date, end: Date): Promise<void> {
      if (presenter.rangesMap[`${start.getTime()}-${end.getTime()}`]) {
        return;
      }

      presenter.rangesMap[`${start.getTime()}-${end.getTime()}`] = [];
      presenter.isLoading = true;

      const events = await Tasks.calendar.getCalendarEvents({
        from: BigInt(start.valueOf()),
        to: BigInt(end.valueOf()),
      });

      if (!events) {
        presenter.rangesMap[`${start.getTime()}-${end.getTime()}`] = null;
        return;
      }

      const newEvents = events.map((event) => {
        return event.allDay ? normalizeAllDay(event) : event;
      });

      presenter.rangesMap[`${start.getTime()}-${end.getTime()}`] = newEvents;

      const eventsFiltered = presenter.data.filter(
        (event) => !(event.start <= end && event.end > start)
      );

      runInAction(() => {
        presenter.data = [...eventsFiltered, ...newEvents];
      });
    },

    addEvent(event: IEvent): void {
      if (presenter.data.find((existingEvent) => existingEvent.id === event.id)) {
        return;
      }
      const newEvents = [...presenter.data, event.allDay ? normalizeAllDay(event) : event];
      presenter.data = newEvents;
    },

    updateEvent(updatedEvent: IEvent): void {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, parentId, ...updates } = updatedEvent;
      const newEvents = presenter.data.map((event) => {
        if (event.id !== id) {
          return event;
        }

        return {
          ...event,
          ...(updates.allDay ? normalizeAllDay(updates) : updates),
        };
      });

      presenter.data = newEvents;
    },

    deleteEventById(id: string): void {
      const newEvents = presenter.data.filter((event) => event.id !== id);
      presenter.data = newEvents;
    },

    deleteEventsByParentId(parentId: string): void {
      const newEvents = presenter.data.filter((event) => {
        return event.parentId !== parentId;
      });
      presenter.data = newEvents;
    },

    reloadEvents(): void {
      presenter.rangesMap = {};

      const { start, end } = getVisibleRange(presenter.currentView, presenter.currentDate) || {};
      presenter.getEventsByRanges(start, end, Navigate.NEXT);
    },

    getEventsByRanges: debounce(async (start: Date, end: Date, action?: NavigateAction) => {
      const additionalRanges = getAdditionalRanges(
        presenter.currentView,
        action || presenter.currentAction,
        start,
        end
      );

      runInAction(() => {
        presenter.currentRange = { start, end };
      });

      const ranges = [{ start, end }, ...additionalRanges];

      for (let i = 0; i < ranges.length; i++) {
        await presenter.getEvents(ranges[i].start, ranges[i].end);
      }

      runInAction(() => {
        presenter.isLoading = false;
      });
    }, GET_EVENTS_DELAY),

    get events(): IEventCalendar[] {
      return presenter.templateEvent
        ? [...presenter.data, presenter.templateEvent]
        : presenter.data;
    },

    // TODO: чтобы не городить такие трансформации, нужно завести стор под ивент календаря
    // и сделать там нужные геттеры для получения нужной даты (конкретной, нужной для отрисовки в календаре, нужной для текстового отображения)
    // При вычислении из даты конца события вычитается 1 миллисекунда для корректной отрисовки ивента на сетке календаря.
    // При отображении данных события будут использоваться поля displayEnd, displayStart для отображения текста с корректной датой
    get gridEvents(): IContentGridCalendarEvent[] {
      return presenter.events.map((event) => {
        let end = event.end;
        const isOvernight = end?.getHours() === 0 && dateService.isNextDay(event.start, event.end);

        if (isOvernight) {
          end = new Date(end.getTime() - 1);
        }

        return {
          ...event,
          end,
          displayEnd: event.end,
          displayStart: event.start,
        };
      });
    },

    updateEventsAfterUpdateRecurrenceSuccess(updateEvent: IEvent, hideSidebar = true): void {
      presenter.currentSelectedEditEvent = null;

      if (hideSidebar) {
        presenterActiveSidebarView.hideSidebar();
      }

      presenter.reloadEvents();
    },

    updateEventAfterUpdateSuccess(updateEvent: IEvent, hideSidebar = true): void {
      presenter.currentSelectedEditEvent = null;

      if (hideSidebar) {
        presenterActiveSidebarView.hideSidebar();
      }

      const eventsFiltered = presenter.data.filter(
        (event) => event.parentId !== updateEvent.parentId
      );

      presenter.data = [
        ...eventsFiltered,
        updateEvent.allDay ? normalizeAllDay(updateEvent) : updateEvent,
      ];
    },

    addEventsAfterCreateRecurrenceSuccess(): void {
      presenter.deleteTemplateEvent();
      presenter.currentSelectedEditEvent = null;
      presenterActiveSidebarView.hideSidebar();

      presenter.reloadEvents();
    },

    addEventAfterCreateSuccess(newEvent: IEvent): void {
      presenter.deleteTemplateEvent();

      presenterActiveSidebarView.hideSidebar();

      presenter.addEvent(newEvent);
    },

    deleteEventsAfterDeleteRecurrenceSuccess(id: string): void {
      presenterActiveSidebarView.hideSidebar();
      presenter.deleteEventsByParentId(id);
    },

    deleteEventAfterDeleteSuccess(id: string): void {
      presenterActiveSidebarView.hideSidebar();

      presenter.deleteEventById(id);
    },

    onCloseViewSidebar(): void {
      presenter.restoreSelectedEvent();
      presenter.deleteTemplateEvent();
      presenter.deleteSelectedEvent();
      presenterActiveSidebarView.hideSidebar();
    },

    onInfoViewSidebar(): void {
      if (!presenter.currentSelectedEditEvent) {
        return;
      }

      presenter.deleteSelectedEvent();
      presenter.data.push({
        ...presenter.currentSelectedEditEvent,
        isSelected: true,
      });
      presenterActiveSidebarView.openInfoViewSidebar();
    },

    deleteTemplateEvent(): void {
      presenter.templateEvent = null;
    },

    deleteSelectedEvent(): void {
      if (!presenter.selectedEvent) {
        return;
      }
      presenter.deleteEventById(presenter.selectedEvent.id);
    },

    restoreSelectedEvent(): void {
      if (presenter.selectedEvent && presenter.currentSelectedEditEvent) {
        presenter.deleteSelectedEvent();
        presenter.data.push(presenter.currentSelectedEditEvent);
      }
    },

    updateTemplateEvent(params: IUpdateEvent): void {
      if (!presenter.templateEvent) {
        return;
      }

      presenter.templateEvent = {
        ...presenter.templateEvent,
        ...params,
      };
    },

    updateSelectedEvent(params: IUpdateEvent): void {
      presenter.data = presenter.data.map((event) => {
        if (event.isSelected) {
          return {
            ...event,
            ...params,
          };
        }
        return event;
      });
    },

    onCreate(): void {
      presenter.restoreSelectedEvent();
      presenter.deleteSelectedEvent();
      presenter.deleteTemplateEvent();
      presenterActiveSidebarView.openCreateViewSidebar();

      const now = dayjs();

      const currentMinutes = now.minute();
      // округляем вверх до ближайших 15
      const roundedMinutes = Math.ceil(currentMinutes / STEP) * STEP;
      let start = now.second(0).millisecond(0);

      if (roundedMinutes === 60) {
        start = start.add(1, 'hour').minute(0);
      } else {
        start = start.minute(roundedMinutes);
      }

      const end = start.add(1, 'hour');
      const id = new Date().getTime().toString();

      presenter.templateEvent = {
        id,
        parentId: id,
        providerEventId: id,
        title: `(${t('calendar_notitle')})`,
        start: start.toDate(),
        end: end.toDate(),
        initiatorEmail: presenter.accounts[0].account.providerEmail,
        account: presenter.accounts[0].account,
        allDay: false,
        isTemplate: true,
        timeZone: '',
        location: '',
        description: '',
        recurrenceType: RepeatEnum.NONE,
        chatLink: '',
        callLink: '',
        reminders: [],
        participants: [],
      };
    },

    async onEditEventAfterDrop(
      dragAndDropData: EventInteractionArgs<IEventCalendar>
    ): Promise<void> {
      const { event: eventDragAndDrop, end, start } = dragAndDropData;

      if (eventDragAndDrop.isTemplate && presenter.templateEvent) {
        presenter.templateEvent = {
          ...presenter.templateEvent,
          start: start as Date,
          end: end as Date,
        };
        return;
      }

      const originalEvent = presenter.data.find((event) => {
        return event.providerEventId === eventDragAndDrop.providerEventId;
      });

      if (!originalEvent) {
        return;
      }

      const eventsUpdated = presenter.data.map((item) =>
        item.providerEventId === eventDragAndDrop.providerEventId
          ? {
              ...item,
              start: start as Date,
              end: end as Date,
            }
          : item
      );

      presenter.data = eventsUpdated;

      const initialParams = {
        title: originalEvent.title,
        description: originalEvent.description,
        allDay: originalEvent.allDay,
        timeZone: originalEvent.timeZone,
        callLink: originalEvent.callLink,
        startTimeTs: originalEvent.allDay
          ? BigInt(
              dayjs(start as Date)
                .utc(true)
                .valueOf()
            )
          : BigInt(dayjs(start).valueOf()),
        endTimeTs: originalEvent.allDay
          ? BigInt(
              dayjs(end as Date)
                .utc(true)
                .valueOf()
            )
          : BigInt(dayjs(end).valueOf()),
        participantEmails: originalEvent.participants.map((participant) => participant.email),
        reminders:
          originalEvent.reminders.length > 0
            ? [
                {
                  type: originalEvent.reminders[0].type,
                  time: BigInt(originalEvent.reminders[0].time),
                },
              ]
            : [],
        location: originalEvent.location,
        chatLink: originalEvent.chatLink,
        account: originalEvent.account,
      };

      const mapEvent =
        originalEvent.recurrenceType === RepeatEnum.NONE
          ? {
              providerEventId: originalEvent.providerEventId,
              ...initialParams,
            }
          : {
              parentId: originalEvent.parentId,
              recurrenceType: originalEvent.recurrenceType,
              ...initialParams,
            };

      const isSuccess = await presenter.onEditEvent(mapEvent, false);

      if (!isSuccess) {
        const eventsUpdated = presenter.data.map((event) =>
          event.id === originalEvent.id ? originalEvent : event
        );

        presenter.data = eventsUpdated;
      }
    },

    async onEditEvent(
      data: IUpdateRecurrenceCalendarEventParams | IUpdateCalendarEventParams,
      hideSidebar?: boolean
    ): Promise<boolean> {
      if ('recurrenceType' in data) {
        const updatedEvent = await Tasks.calendar.updateRecurrenceCalendarEvent(data);

        if (updatedEvent) {
          presenter.updateEventsAfterUpdateRecurrenceSuccess(updatedEvent, hideSidebar);
          return true;
        } else {
          return false;
        }
      } else {
        const updatedEvent = await Tasks.calendar.updateCalendarEvent(data);

        if (updatedEvent) {
          presenter.updateEventAfterUpdateSuccess(updatedEvent, hideSidebar);
          return true;
        } else {
          return false;
        }
      }
    },

    updateEventAfterUpdateParticipantStatusSuccess(event: IEventCalendar): void {
      if (event.recurrenceType !== RepeatEnum.NONE) {
        presenter.updateEventsAfterUpdateRecurrenceSuccess(event);
      } else {
        presenter.updateEventAfterUpdateSuccess(event);
      }
    },

    get selectedEvent(): IEventCalendar | undefined {
      return presenter.data.find((event) => event.isSelected);
    },

    get isDayView(): boolean {
      return presenter.currentView === Views.DAY;
    },

    get isWeekView(): boolean {
      return presenter.currentView === Views.WEEK;
    },

    get isInitiator(): boolean {
      if (!presenter.selectedEvent) {
        return false;
      }
      return (
        presenter.selectedEvent.account.providerEmail === presenter.selectedEvent?.initiatorEmail
      );
    },

    get isTimeGrid(): boolean {
      return presenter.isDayView || presenter.isWeekView;
    },

    getMyEventStatus(participants: IParticipant[], providerEmail: string): ConfirmationType {
      const participant = participants.find((participant) => providerEmail === participant.email);

      return participant?.confirmationType || ConfirmationType.NO_ANSWER;
    },

    get myEventStatusBySelectedEvent(): ConfirmationType {
      return presenter.getMyEventStatus(
        presenter.selectedEvent?.participants || [],
        presenter.selectedEvent?.account.providerEmail || ''
      );
    },

    // прыжок к дате
    jumpTo(date: Date): void {
      const dateTo = new Date(date);

      if (presenter.currentView === Views.DAY) {
        dateTo.setHours(0, 0, 0, 0);
      }
      const { start, end } = getVisibleRange(presenter.currentView, dateTo);

      presenter.getEventsByRanges(start, end);

      Entities.calendar.setCurrentDate(dateTo);

      presenter.currentDate = dateTo;
    },

    openDay(date: Date): void {
      presenter.changeView(Views.DAY);
      presenter.jumpTo(date);
    },

    onDrillDown(date: Date): void {
      if (presenter.isDayView) {
        return;
      }

      presenter.openDay(date);
    },

    changeView(view: View): void {
      storage.calendar.setCalendarCurrentView(view);

      presenter.currentSelectedEditEvent = null;
      presenter.data = [];
      presenter.rangesMap = {};
      presenterActiveSidebarView.hideSidebar();

      presenter.currentView = view;
    },

    onNavigate(date: Date, view: View, action: NavigateAction): void {
      Entities.calendar.setCurrentDate(date);

      presenter.restoreSelectedEvent();
      presenter.deleteTemplateEvent();
      presenter.deleteSelectedEvent();
      presenterActiveSidebarView.hideSidebar();

      presenter.currentDate = new Date(date);
      presenter.currentAction = action;
    },

    onSelectEvent(selectedEvent: IEventCalendar): void {
      if (selectedEvent.isTemplate || selectedEvent.isSelected) {
        return;
      }

      let currentSelectedEditEvent = selectedEvent;

      presenter.restoreSelectedEvent();
      presenter.deleteTemplateEvent();
      presenter.deleteSelectedEvent();
      presenterActiveSidebarView.openInfoViewSidebar();

      presenter.data = presenter.data.map((event) => {
        if (event.id === selectedEvent.id) {
          currentSelectedEditEvent = event;

          return {
            ...currentSelectedEditEvent,
            isSelected: true,
          };
        }

        return event;
      });

      presenter.currentSelectedEditEvent = currentSelectedEditEvent;
    },

    onSelectSlot(info: SlotInfo): void {
      presenter.restoreSelectedEvent();
      presenter.deleteSelectedEvent();
      presenter.deleteTemplateEvent();
      presenterActiveSidebarView.openCreateViewSidebar();
      const id = new Date().getTime().toString();

      presenter.templateEvent = {
        id,
        parentId: id,
        providerEventId: id,
        title: `(${t('calendar_notitle')})`,
        start: info.start,
        end:
          info.action === 'click' && !checkAllDayRange(info.start, info.end)
            ? dayjs(info.start).add(1, 'hour').toDate()
            : info.end,
        allDay: checkAllDayRange(info.start, info.end),
        initiatorEmail: presenter.accounts[0].account.providerEmail,
        account: presenter.accounts[0].account,
        isTemplate: true,
        timeZone: '',
        location: '',
        description: '',
        recurrenceType: RepeatEnum.NONE,
        chatLink: '',
        callLink: '',
        reminders: [],
        participants: [],
      };
    },

    onRangeChange(range: IRange): void {
      if (Array.isArray(range) && range.length) {
        const start = range[0];
        let end = dayjs(range[range.length - 1])
          .endOf('day')
          .toDate();

        if (presenter.currentView === Views.DAY) {
          end = dayjs(start).endOf('day').toDate();
        }

        presenter.getEventsByRanges(start, end);
      } else if (!Array.isArray(range) && range?.start && range?.end) {
        presenter.getEventsByRanges(range.start, range.end);
      }
    },

    minimizeSideBar(): void {
      presenter.isSideBarMinimized = !presenter.isSideBarMinimized;
    },

    handleCreateRealtimeEvent(event: IEvent): void {
      if (isRecurringEvent(event)) {
        presenter.reloadEvents();
        return;
      }
      presenter.addEvent(event);
    },

    handleUpdateRealtimeEvent(event: IEvent): void {
      if (isRecurringEvent(event)) {
        presenter.reloadEvents();
        return;
      }

      // При создании события с другого аккаунта и добавлении своего пользователя в участники,
      // отправляется только один рилтайм ивент с типом UPDATE. Соответственно того ивента,
      // который надо проапдэйтить не существует на текущем аккаунте.
      // В этом случае необходимо добавить событие, а не пытаться апдэйтить несуществующее
      if (!presenter.data.find((dataEvent) => dataEvent.id === event.id)) {
        presenter.addEvent(event);
        return;
      }

      presenter.updateEvent(event);
    },

    handleDeleteRealtimeEvent(event: IEvent): void {
      if (isRecurringEvent(event)) {
        presenter.reloadEvents();
        return;
      }
      presenter.deleteEventById(event.id);
    },
  }));

  useEffect(() => {
    presenter.initialize(presenter.initialCalendarParams);
    presenterDragAndDrop.setEditEventAfterDrop(presenter.onEditEventAfterDrop);
  }, []);

  useEffect(() => {
    if (!presenter.isTimeGrid || !presenter.currentRange || presenter.events.length === 0) {
      return;
    }

    presenterScrollToFirstEvent.calculateFirstEvent(
      presenter.currentRange,
      presenter.data,
      presenter.currentDate,
      presenter.currentView
    );
  }, [
    presenter.isTimeGrid,
    presenter.events,
    presenter.currentRange,
    presenter.currentView,
    presenter.currentDate,
  ]);

  useEffect(() => {
    const realTimeEventSubscriptions: (() => void)[] = [];

    realTimeEventSubscriptions.push(
      EventBus.calendar.subscribeCreateCalendarEvent(presenter.handleCreateRealtimeEvent)
    );
    realTimeEventSubscriptions.push(
      EventBus.calendar.subscribeUpdateCalendarEvent(presenter.handleUpdateRealtimeEvent)
    );
    realTimeEventSubscriptions.push(
      EventBus.calendar.subscribeDeleteCalendarEvent(presenter.handleDeleteRealtimeEvent)
    );

    return () => {
      realTimeEventSubscriptions.forEach((unsubscribeFn) => unsubscribeFn());
    };
  }, []);

  return {
    presenter,
    presenterActiveSidebarView,
    presenterDragAndDrop,
    presenterScrollToFirstEvent,
  };
};
